import { ArrowUpRight, Globe, Info } from 'lucide-react';
import { ApiError } from '../../api/envelope';
import type { Campaign } from '../../api/types';
import { money } from '../../lib/commsFormat';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import { useComms } from './context';

/**
 * What a campaign's tagged links brought to the website — the Results half of
 * linking campaigns to website analytics.
 *
 * The delivery funnel above stops at the inbox: sent, opened, bounced. Whether
 * the mail moved anybody is the question an operator actually ran it to answer,
 * and the website is where that happens. The figures are the Analytics
 * Campaigns card's own rows for this campaign's tracking name, so the two
 * screens agree, and "Open in Analytics" goes to that card.
 *
 * Email only — a text carries no tracking (the parameters would cost a
 * segment) — and only for somebody whose `analytics` is not off: the server
 * refuses everyone else, and a refusal here is "not offered", never an error
 * on somebody else's screen.
 */
export function CampaignWebsiteCard({
  campaign,
  onOpenAnalytics,
}: {
  campaign: Campaign;
  /** Absent where Analytics cannot be reached from here — the link is then not drawn. */
  onOpenAnalytics?: () => void;
}) {
  const { source, capabilities } = useComms();
  const { fmtDate } = useTimeFormat();
  const offered = campaign.channel !== 'sms' && capabilities?.access.features.analytics !== 'off';
  const website = useLoad(
    () => (offered ? source.api.getCampaignWebsite(campaign.id) : Promise.resolve(null)),
    // `startedAt` too: the card says "counting starts when it sends" until it has.
    [source, campaign.id, offered, campaign.startedAt],
  );

  if (!offered) return null;
  if (website.error) {
    // Forbidden is "not offered"; anything else is said once, quietly, rather
    // than a red box over a campaign whose delivery is fine.
    if (website.error instanceof ApiError && website.error.kind === 'forbidden') return null;
    return (
      <div className="cmg-insight">
        <Info size={15} />
        <span>Could not load what this campaign brought to your website.</span>
      </div>
    );
  }
  const data = website.data;
  if (!data) return null;

  if (!data.tracking) {
    return (
      <div className="cmg-insight">
        <Info size={15} />
        <span>
          This campaign’s links were not tracked, so its website visits are counted under Email with
          every other mail. Turn on <b>Website tracking</b> in the builder’s Message step next time, and
          Results shows the visits and reservations it brings.
        </span>
      </div>
    );
  }

  const analyticsLink = onOpenAnalytics ? (
    <button type="button" className="cbtn" onClick={onOpenAnalytics}>
      Open in Analytics
      <ArrowUpRight size={13} />
    </button>
  ) : null;

  return (
    <div className="cmg-card cmg-website" data-card="website" style={{ gap: 10 }}>
      <div className="cmg-website-head">
        <Globe size={14} />
        <span className="cmg-website-title">On your website</span>
        <code className="cmg-website-tag" title="The tracking name its links carry">
          {data.tracking}
        </code>
        <div style={{ flex: 1 }} />
        {analyticsLink}
      </div>
      {data.installs === 0 ? (
        <div className="cmg-hint">
          Its links carry the name, but nothing is counting visits on your website yet. Set up
          Analytics on your site and the visits from this campaign and the next show up here.
        </div>
      ) : !data.since ? (
        <div className="cmg-hint">Counting starts when it sends.</div>
      ) : (
        <>
          <div className="cstat-row">
            <div className="cstat">
              <div className="cstat-label">Visits</div>
              <div className="cstat-value">{data.sessions.toLocaleString()}</div>
            </div>
            <div className="cstat">
              <div className="cstat-label">Pages viewed</div>
              <div className="cstat-value">{data.pageviews.toLocaleString()}</div>
            </div>
            <div className="cstat">
              <div className="cstat-label">Reservations</div>
              <div className={`cstat-value${data.bookings > 0 ? ' green' : ''}`}>
                {data.bookings.toLocaleString()}
              </div>
            </div>
            <div className="cstat">
              <div className="cstat-label">Reservation value</div>
              <div className="cstat-value">{money(data.bookingValue)}</div>
            </div>
          </div>
          <div className="cmg-hint">
            Visits from links tagged <code>{data.tracking}</code> since this campaign started on{' '}
            {fmtDate(campaign.startedAt)}
            {campaign.resendOfCampaignId === null
              ? ' — any resend of it carries the same name, so its visits are counted here too'
              : ' — the name is shared with the campaign this resends, so visits from either mail count here'}
            . A visit is counted when somebody clicks through; reading the email on its own is the
            open rate above.
          </div>
        </>
      )}
    </div>
  );
}
